import { featureCollection } from '@turf/turf';
import { computed, ref, watch } from 'vue';
import { useSelectedLogSorterStore } from '../stores/selectedLogSorterStore';
import { storeToRefs } from 'pinia';
import { useSelectedShapeStore } from '../stores/selectedShapeStore';
import { useRafFn } from '@vueuse/core';
import type { Coordinate } from 'shared/map/domain/coordinate';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Text from 'ol/style/Text.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { getDragPan } from 'shared/map/domain/interactions';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { Color } from 'shared/common/domain/color';
import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';
import { LogSorterNumberSequence } from 'shared/logSorter/domain/logSorterNumberSequence';

export function usePreviewLogsorterLayer() {
  const store = useSelectedLogSorterStore();
  const selectedShapeStore = useSelectedShapeStore();

  const moving = ref<boolean>(false);
  const hover = ref<boolean>(false);
  let offset: null | Coordinate = null;
  let newCoordinate: null | Coordinate = null;
  const geojson = new GeoJSON();
  const dragPanEnabled = computed(() => !moving.value);

  const { operation, bins } = storeToRefs(store);

  const shapes = computed(() => {
    if (bins.value === undefined) return featureCollection([]);

    const features: GeoJSON.Feature[] = [];

    let id = 1;

    for (const bin of bins.value) {
      bin.geoJson.shape.id = id;
      id += 1;

      features.push(bin.geoJson.shape);
    }

    return featureCollection(features);
  });

  const geofences = computed(() => {
    if (bins.value === undefined) return featureCollection([]);

    const features: GeoJSON.Feature[] = [];

    for (const bin of bins.value) {
      features.push(bin.geoJson.geofence);
    }

    return featureCollection(features);
  });

  watch(operation, (newOperation) => {
    switch (newOperation) {
      case 'create':
        selectedShapeStore.selectionEnabled = false;
        break;
      case 'edit':
        selectedShapeStore.selectionEnabled = false;
        break;
      case 'none':
        selectedShapeStore.selectionEnabled = true;
        break;
    }
  });

  useRafFn(() => {
    if (newCoordinate) {
      store.newCoordinate(newCoordinate);
      newCoordinate = null;
    }
  });

  const cursor = computed(() => {
    if (moving.value) return 'grabbing';
    if (hover.value) return 'move';
    return undefined;
  });

  function attach(map: OLMap) {
    const { source, geofenceSource } = createLayer(map);
    const element = map.getTargetElement();
    const dragPan = getDragPan(map);

    watch(shapes, (newShapes) => {
      source.clear();
      const features = geojson.readFeatures(newShapes);
      source.addFeatures(features);
    });

    watch(geofences, (newGeofences) => {
      geofenceSource.clear();
      const features = geojson.readFeatures(newGeofences);
      geofenceSource.addFeatures(features);
    });

    watch(cursor, (newCursor) => {
      if (newCursor) {
        element.style.cursor = newCursor;
      } else {
        element.style.removeProperty('cursor');
      }
    });

    watch(dragPanEnabled, (enabled) => {
      if (!dragPan) return;
      dragPan.setActive(enabled);
    });

    element.addEventListener('mousemove', (ev) => {
      hover.value = false;
      let mouseCoordinate;
      try {
        mouseCoordinate = map.getEventCoordinate(ev);
      } catch {
        return; // Map projection not yet initialized
      }
      if (!mouseCoordinate) return;

      const features = source.getFeaturesAtCoordinate(mouseCoordinate);
      hover.value = features.length > 0;

      const coordinate = store.coordinate;
      if (!coordinate || !moving.value) return;

      if (!offset) {
        offset = {
          longitude: coordinate.longitude - mouseCoordinate[0]!,
          latitude: coordinate.latitude - mouseCoordinate[1]!,
        };
      }

      newCoordinate = {
        longitude: mouseCoordinate[0]! + offset.longitude,
        latitude: mouseCoordinate[1]! + offset.latitude,
      };
    });

    element.addEventListener('mousedown', (ev) => {
      let coordinate;
      try {
        coordinate = map.getEventCoordinate(ev);
      } catch {
        return; // Map projection not yet initialized
      }
      if (!coordinate) return;

      const features = source.getFeaturesAtCoordinate(coordinate);
      if (features.length === 0) return;

      ev.preventDefault();

      element.style.cursor = 'grab';
      offset = null;

      moving.value = true;
    });

    element.addEventListener('mouseup', () => {
      moving.value = false;
    });

    return source;
  }

  return {
    attach,
  };
}

function createLayer(map: OLMap) {
  const styleConfig = new Style({
    fill: new Fill({
      color: '#fef3c7',
    }),
    stroke: new Stroke({
      color: '#000000',
    }),
    text: new Text({
      overflow: false,
      font: '16px Calibri,sans-serif',
      fill: new Fill({
        color: '#000',
      }),
    }),
  });

  const source = new VectorSource();

  const layer = new VectorLayer({
    source,
    style: (feature: FeatureLike) => {
      const id = String(feature.getId() ?? '');
      styleConfig.getText()?.setText(id);

      return styleConfig;
    },
  });

  const geofenceSource = new VectorSource();
  const geofenceLayer = new VectorLayer({
    source: geofenceSource,
    style: new Style({
      fill: new Fill({
        color: Color.setAlpha('#d8b4fe', 0.6),
      }),
      stroke: new Stroke({
        color: Color.setAlpha('#000000', 0.6),
      }),
    }),
  });

  map.addLayer(geofenceLayer);
  map.addLayer(layer);

  return {
    source,
    geofenceSource,
  };
}
